import { nearestImpitChromeProfile } from './browser-fingerprint';

/**
 * What this product says about itself when it fetches something, and what it
 * will accept back.
 *
 * Four files carried the same browser user-agent string, two carried the same
 * list of image types, and two more the same page timeout and viewport. None of
 * the copies disagreed yet, which is the only reason none of it had shown up as
 * a bug: a duplicate constant is not wrong until the day somebody changes one.
 * Then it is wrong in the place nobody edited, which is the place nobody looks.
 */

/**
 * A real browser's user-agent, for fetching a page that would otherwise refuse
 * us.
 *
 * Not a disguise so much as a statement of capability: many pages serve a
 * stripped or challenge version to anything that does not look like a browser,
 * and what we want back is the page a person would see, because that is the
 * page whose content is being argued about.
 *
 * Used by the liveness re-check, the media extractor and both browser workers.
 * Same major as the impit profile, not a literal: a site's clearance cookie is
 * bound to the user-agent that earned it, and impit replays it with its own.
 */
export const BROWSER_USER_AGENT = `Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/${nearestImpitChromeProfile().replace('chrome', '')}.0.0.0 Safari/537.36`;

/**
 * The rest of what a browser sends beside its user-agent: a request with only
 * a UA is one many sites answer with a stripped or block page. Honest headers —
 * what we accept, the language we read, the navigation context — not a claim to
 * be a particular build, which is a fingerprint we do not forge.
 */
export const BROWSER_HEADERS: Record<string, string> = {
  accept:
    'text/html,application/xhtml+xml,application/xml;q=0.9,image/avif,image/webp,image/apng,*/*;q=0.8',
  'accept-language': 'en-US,en;q=0.9',
  'accept-encoding': 'gzip, deflate, br',
  'upgrade-insecure-requests': '1',
  'sec-fetch-dest': 'document',
  'sec-fetch-mode': 'navigate',
  'sec-fetch-site': 'none',
  'sec-fetch-user': '?1',
};

/** The user-agent and the rest, together: what to spread into a request's headers. */
export const BROWSER_REQUEST_HEADERS: Record<string, string> = {
  'user-agent': BROWSER_USER_AGENT,
  ...BROWSER_HEADERS,
};

/**
 * What we call ourselves when we are not pretending to be anybody.
 *
 * For fetching something a client pointed us at (her own page), where there is
 * nothing to get past and no reason not to be honest about who is asking.
 */
export const BOT_USER_AGENT = 'Mozilla/5.0 (compatible; SnilyBot/1.0; +https://snily.io/bot)';

/** How long a page gets to answer a browser before it is abandoned. */
export const PAGE_TIMEOUT_MS = 30_000;

/** The window a captured page is rendered in. Wide enough to look like a desktop. */
export const BROWSER_VIEWPORT = { width: 1280, height: 900 } as const;

/**
 * The image formats this product will store.
 *
 * One list for uploads and for anything fetched, because they end up in the
 * same bucket and go to the same fingerprinting worker. A format accepted in
 * one path and refused in the other is a difference nobody chose.
 */
export const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'] as const;


/** The extension an object gets, so storage and support tooling behave. */
export const IMAGE_EXTENSIONS: Record<string, string> = {
  'image/jpeg': '.jpg',
  'image/png': '.png',
  'image/webp': '.webp',
  'image/gif': '.gif',
};

/**
 * An avatar is a thumbnail. Anything larger is not the thing we asked for, and
 * both the plain fetch and the browser fetch need to agree about that.
 */
export const MAX_AVATAR_BYTES = 8 * 1024 * 1024;

/**
 * Reference images, and the ceiling the fingerprinting worker enforces.
 *
 * Read by both sides of the upload — the API refuses anything over this, and
 * the client's own file picker warns before a person waits through an upload
 * that was always going to be refused — and it has to be the worker's own
 * number (`MAX_IMAGE_BYTES` in `apps/worker-py/main.py`, checked by
 * `shared-caps.test.mjs`). It was 25MB against the worker's 15MB, so a photo
 * in between was stored, refused by the worker, retried five times and left
 * reading "processing" for ever: a picture she waited to upload that protects
 * nothing and says nothing about why.
 */
export const MAX_IMAGE_BYTES = 15 * 1024 * 1024;

/** A signed letter is a scan or a photo of one page. */
export const MAX_DOCUMENT_BYTES = 10 * 1024 * 1024;

/** What a signed authorisation letter may be uploaded as. */
export const DOCUMENT_TYPES = ['application/pdf', 'image/jpeg', 'image/png'] as const;


/**
 * A page that is a gate rather than the page: Cloudflare's, DDoS-Guard's, and
 * the markers every variant of them carries. It answers 200, so reading it as
 * content reports a refusal as an empty page.
 */
const CHALLENGE_MARKERS = [
  'just a moment',
  'checking your browser',
  'enable javascript and cookies',
  'cf-browser-verification',
  'cf_chl_opt',
  'challenge-platform',
  'attention required! | cloudflare',
  'ddos-guard',
];

/** Which gate it is, or null for a page. The head is enough: a challenge is the whole response. */
export function challengeMarkerIn(html: string): string | null {
  const head = html.slice(0, 4000).toLowerCase();
  return CHALLENGE_MARKERS.find((marker) => head.includes(marker)) ?? null;
}

export function looksLikeChallenge(html: string): boolean {
  return challengeMarkerIn(html) !== null;
}
